import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Palette,
  FileText,
  Save,
  Check,
  Type,
  LayoutGrid as LayoutIcon,
} from 'lucide-react';

/**
 * ResumeStudioShowcase
 * ----------------------------------------------------------------------------
 * Landing-page section that showcases the Resume Studio to prospective users.
 *
 * Design intent (senior-level details):
 *   - Matches the site-wide Swiss/light aesthetic (no dark-mode deviation).
 *   - Satoshi headings, Inter body, #002fa7 primary accent, hairline borders.
 *   - Depicts the REAL Studio: sidebar controls (Theme / Typography / Layout),
 *     10 swatches with the active one ringed, and a live resume preview with
 *     proper typographic hierarchy (name → contact → section heading w/
 *     accent underline → role subheading → italic date line → bullets).
 *   - macOS-style browser chrome + deep-lensed shadow convey "real product".
 *   - Ambient dot-grid gives depth without noise. Respects reduced-motion.
 *   - Every interactive element has a stable data-testid.
 * ----------------------------------------------------------------------------
 */

const ACCENT_SWATCHES = [
  '#002fa7', // Primary — active
  '#0f766e',
  '#7c3aed',
  '#ea580c',
  '#0891b2',
  '#be185d',
  '#65a30d',
  '#4338ca',
  '#b45309',
  '#0f1115',
];

const FEATURE_PILLS = [
  {
    id: 'themes',
    icon: Palette,
    title: '12 industry themes',
    desc: 'Tech, Executive, Creative, Medical, Legal, Academic, Finance, Sales, Government, Nonprofit, Classic, Minimal.',
  },
  {
    id: 'export',
    icon: FileText,
    title: 'Pixel-identical PDF & Word',
    desc: 'What you see is exactly what recruiters download. No formatting surprises, ever.',
  },
  {
    id: 'autosave',
    icon: Save,
    title: 'Notion-style inline editing',
    desc: 'Click anywhere on your resume and just type. Autosaves every 1.5 seconds.',
  },
];

// ============================================================================
// Browser mockup — the visual centerpiece. Pure HTML/CSS, no assets.
// ============================================================================
const BrowserMockup = () => (
  <div
    className="relative"
    data-testid="studio-mockup"
    aria-hidden="true" /* decorative — real UX is on /resume/:id/studio */
  >
    {/* Soft ambient halo behind the window */}
    <div
      className="absolute -inset-8 -z-10 blur-3xl opacity-40"
      style={{
        background:
          'radial-gradient(45% 55% at 50% 50%, rgba(0,47,167,0.18), transparent 72%)',
      }}
    />

    <div
      className="bg-white border border-[#e5e7eb] overflow-hidden"
      style={{
        boxShadow:
          '0 1px 0 rgba(15,17,21,0.04), 0 24px 60px -30px rgba(0,47,167,0.28), 0 48px 80px -40px rgba(15,17,21,0.18)',
      }}
    >
      {/* ── Browser chrome ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#e5e7eb] bg-[#fafafa]">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        </div>
        <div className="flex-1 mx-2 px-3 py-1 bg-white border border-[#e5e7eb] text-xs text-[#6b7280] font-mono truncate">
          ascendforyou.net/resume/jordan-chen/studio
        </div>
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#002fa7]">
          Studio
        </div>
      </div>

      {/* Body: sidebar + preview — sidebar is hidden on mobile so the
          preview remains legible. The sidebar is decorative; real editing
          happens at /resume/:id/studio. */}
      <div className="grid md:grid-cols-[200px_1fr] min-h-[460px]">
        {/* Sidebar — md+ only */}
        <aside className="hidden md:block border-r border-[#e5e7eb] bg-[#fafafa] p-4 text-[12px]">
          {/* THEME */}
          <div className="mb-5">
            <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#6b7280]">
              <Palette size={11} />
              Theme
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {ACCENT_SWATCHES.map((hex, i) => (
                <button
                  key={hex}
                  type="button"
                  tabIndex={-1}
                  aria-label={`Accent color ${i + 1}`}
                  className="w-6 h-6 rounded-full border border-white"
                  style={{
                    background: hex,
                    boxShadow:
                      i === 0
                        ? '0 0 0 2px #002fa7, 0 0 0 4px #fff'
                        : '0 0 0 1px #e5e7eb',
                  }}
                />
              ))}
            </div>
          </div>

          {/* TYPOGRAPHY */}
          <div className="mb-5">
            <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#6b7280]">
              <Type size={11} />
              Typography
            </div>
            <div className="flex border border-[#e5e7eb] bg-white">
              {['Sans', 'Serif', 'Mono'].map((label, i) => (
                <div
                  key={label}
                  className={`flex-1 text-center py-1 text-[11px] font-semibold ${
                    i === 0 ? 'bg-[#002fa7] text-white' : 'text-[#4b5563]'
                  }`}
                >
                  {label}
                </div>
              ))}
            </div>
            <div className="mt-2 flex border border-[#e5e7eb] bg-white">
              {['S', 'M', 'L'].map((label, i) => (
                <div
                  key={label}
                  className={`flex-1 text-center py-1 text-[11px] font-semibold ${
                    i === 1 ? 'bg-[#0f1115] text-white' : 'text-[#4b5563]'
                  }`}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* LAYOUT */}
          <div>
            <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#6b7280]">
              <LayoutIcon size={11} />
              Layout
            </div>
            <div className="space-y-1.5">
              {[
                { label: 'Letter', active: true },
                { label: 'A4', active: false },
                { label: 'Compact margins', active: false },
              ].map((opt) => (
                <div
                  key={opt.label}
                  className={`flex items-center gap-1.5 px-2 py-1 text-[11px] ${
                    opt.active
                      ? 'bg-white border border-[#e5e7eb] text-[#0f1115] font-semibold'
                      : 'text-[#6b7280]'
                  }`}
                >
                  {opt.active && <Check size={11} className="text-[#002fa7]" />}
                  {!opt.active && <span className="w-[11px]" />}
                  {opt.label}
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Resume preview */}
        <div className="relative bg-[#f4f5f7] p-5 sm:p-6">
          {/* Top-of-pane status row — sits above the page on mobile so it
              never covers content, floats absolutely on md+ for the
              polished editor-feel. */}
          <div className="flex items-center justify-between mb-3 md:mb-0 md:absolute md:top-4 md:right-4 md:left-auto md:z-10">
            <div
              className="inline-flex items-center gap-1.5 bg-white border border-[#e5e7eb] px-2.5 py-1 text-[11px] font-semibold text-[#0f766e]"
              data-testid="studio-autosave-pill"
            >
              <span
                className="studio-autosave-dot w-1.5 h-1.5 rounded-full bg-[#10b981]"
                aria-hidden="true"
              />
              Autosaved 2s ago
            </div>
          </div>

          {/* The "page" — serif body to signal document vs. sans UI chrome */}
          <div
            className="bg-white mx-auto max-w-[480px] p-5 sm:p-6 border border-[#e5e7eb]"
            style={{
              fontFamily:
                '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
              boxShadow: '0 12px 24px -16px rgba(15,17,21,0.15)',
            }}
          >
            {/* Resume header */}
            <div className="text-center mb-4 pb-3 border-b border-[#e5e7eb]">
              <div className="text-[20px] font-extrabold tracking-tight text-[#0f1115] leading-tight">
                Jordan Chen
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#002fa7] mt-1">
                Senior Product Designer
              </div>
              <div className="text-[10px] text-[#6b7280] mt-1.5">
                jordan@chen.co · San Francisco · linkedin.com/in/jchen
              </div>
            </div>

            {/* SUMMARY */}
            <section className="mb-3">
              <h3 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#0f1115] mb-1.5 pb-1 border-b-2 border-[#002fa7] inline-block">
                Summary
              </h3>
              <p className="text-[10.5px] leading-[1.55] text-[#374151]">
                Product designer with 8+ years shipping consumer SaaS. Led the
                design of pricing flows that lifted paid conversion 31%
                quarter-over-quarter.
              </p>
            </section>

            {/* EXPERIENCE */}
            <section>
              <h3 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#0f1115] mb-1.5 pb-1 border-b-2 border-[#002fa7] inline-block">
                Experience
              </h3>

              <div className="mb-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[11.5px] font-bold text-[#0f1115]">
                    Lead Product Designer · Stripe
                  </div>
                  <div className="text-[9.5px] italic text-[#6b7280] shrink-0">
                    2022 — Present
                  </div>
                </div>
                <ul className="mt-1 space-y-0.5 text-[10.5px] leading-[1.5] text-[#374151] list-disc pl-4">
                  <li>
                    Redesigned checkout flow; increased completion rate from
                    62% to 81%.
                  </li>
                  <li>
                    Mentored 4 designers and launched the internal component
                    library used by 120+ engineers.
                  </li>
                </ul>
              </div>

              <div>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[11.5px] font-bold text-[#0f1115]">
                    Senior Designer · Figma
                  </div>
                  <div className="text-[9.5px] italic text-[#6b7280] shrink-0">
                    2019 — 2022
                  </div>
                </div>
                <ul className="mt-1 space-y-0.5 text-[10.5px] leading-[1.5] text-[#374151] list-disc pl-4">
                  <li>
                    Shipped auto-layout v2; cited in 40+ customer case studies.
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Floating ATS-score badge — below the page on mobile (inline),
              floating on md+. */}
          <div
            className="mt-4 md:mt-0 md:absolute md:bottom-4 md:right-4 bg-[#0f1115] text-white px-3 py-2 inline-flex items-center gap-2 w-fit mx-auto md:mx-0"
            data-testid="studio-ats-badge"
          >
            <div className="text-[9px] uppercase tracking-[0.16em] text-white/60 leading-none">
              ATS Score
            </div>
            <div className="text-[20px] font-extrabold leading-none tabular-nums">
              92
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================================
// Main section
// ============================================================================
const ResumeStudioShowcase = () => {
  const reduce = useReducedMotion();
  const fadeUp = { opacity: 0, y: 20 };
  const fadeUpV = { opacity: 1, y: 0 };
  const fadeRight = { opacity: 0, x: 24 };
  const fadeRightV = { opacity: 1, x: 0 };

  return (
    <section
      id="resume-studio"
      className="relative py-20 lg:py-28 border-b border-[#e5e7eb] bg-white overflow-hidden"
      data-testid="resume-studio-showcase"
    >
      {/* Ambient dot-grid — depth without noise */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'radial-gradient(#d1d5db 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage:
            'radial-gradient(ellipse 80% 70% at 50% 40%, #000 35%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 70% at 50% 40%, #000 35%, transparent 80%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* ── Copy column ─────────────────────────────────────────── */}
          <motion.div
            className="lg:col-span-5"
            initial={reduce ? false : fadeUp}
            whileInView={fadeUpV}
            transition={{ duration: 0.55 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4">
              <span className="w-8 h-px bg-[#002fa7]" />
              Resume Studio
            </span>

            <h2 className="font-satoshi text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#0f1115] leading-[1.05] mb-5">
              Polish your resume to the pixel —{' '}
              <span className="text-[#002fa7]">without the fiddly part.</span>
            </h2>

            <p className="text-[#4b5563] leading-relaxed mb-6 max-w-lg">
              A WYSIWYG editor built for job seekers who know what a good resume
              looks like. Switch between twelve industry-tuned themes, tune the
              typography, and export a pixel-identical PDF or Word doc in one
              click.
            </p>

            <ul className="space-y-2.5 mb-8">
              {[
                'Twelve themes tuned for Tech, Executive, Creative, Medical, Legal, and more',
                'Live preview matches the final PDF and Word export exactly',
                'Notion-style inline editing — click anywhere and just type',
                'Autosaves every 1.5 seconds. Never lose a word.',
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-[#374151]"
                >
                  <Check
                    size={18}
                    className="text-[#002fa7] shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span className="leading-snug">{line}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/register"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="studio-showcase-cta-primary"
              >
                <span>Try Resume Studio free</span>
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/free-ats-check"
                className="inline-flex items-center gap-1 text-sm font-semibold text-[#0f1115] underline decoration-[#002fa7] decoration-2 underline-offset-4 hover:decoration-4 transition-all"
                data-testid="studio-showcase-cta-secondary"
              >
                Score my current resume first
              </Link>
            </div>

            <p className="mt-4 text-xs text-[#6b7280]">
              No credit card. Create a free resume in under 60 seconds.
            </p>
          </motion.div>

          {/* ── Mockup column ───────────────────────────────────────── */}
          <motion.div
            className="lg:col-span-7"
            initial={reduce ? false : fadeRight}
            whileInView={fadeRightV}
            transition={{ duration: 0.55, delay: 0.1 }}
            viewport={{ once: true, amount: 0.25 }}
          >
            <BrowserMockup />
          </motion.div>
        </div>

        {/* ── Feature pill strip ──────────────────────────────────── */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#e5e7eb] bg-white">
          {FEATURE_PILLS.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.id}
                className={`p-6 lg:p-7 ${
                  i < FEATURE_PILLS.length - 1
                    ? 'md:border-r border-[#e5e7eb]'
                    : ''
                }`}
                initial={reduce ? false : fadeUp}
                whileInView={fadeUpV}
                transition={{ duration: 0.45, delay: 0.1 + i * 0.08 }}
                viewport={{ once: true, amount: 0.5 }}
                data-testid={`studio-showcase-feature-${item.id}`}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 bg-[#002fa7]/10 text-[#002fa7] mb-3">
                  <Icon size={18} />
                </div>
                <h3 className="font-satoshi text-base font-bold text-[#0f1115] mb-1.5">
                  {item.title}
                </h3>
                <p className="text-sm text-[#4b5563] leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Scoped CSS for the autosave pulse — respects reduced-motion */}
      <style>{`
        @keyframes studioAutosavePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
          50%      { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
        }
        .studio-autosave-dot {
          animation: studioAutosavePulse 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .studio-autosave-dot { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default ResumeStudioShowcase;
